import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_swiss-logistics/artifacts/qc38gmzs_LOGO-TRANSP.png";

const navLinks = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'Services', href: '#services' },
  { label: 'Devis', href: '#devis' },
  { label: 'Témoignages', href: '#temoignages' },
  { label: 'Contact', href: '#contact' },
];

const sectionIds = ['accueil', 'services', 'devis', 'temoignages', 'contact'];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('accueil');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const scrollPos = window.scrollY + 120;
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-40 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <a href="#accueil" onClick={(e) => scrollTo(e, '#accueil')} data-testid="header-logo" className="flex-shrink-0">
          <img
            src={LOGO_URL}
            alt="Janyel's Transport Service Sàrl — Transport et déménagement en Suisse"
            className="h-14 md:h-16 w-auto"
          />
        </a>

        {/* Desktop Nav - centered */}
        <nav className="hidden lg:flex items-center justify-center flex-1 gap-6" data-testid="desktop-nav">
          {navLinks.map((link) => {
            const id = link.href.replace('#', '');
            const isActive = activeSection === id;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className={`nav-link text-sm font-medium uppercase tracking-widest transition-colors ${
                  isActive ? 'text-white' : 'text-white/50 hover:text-white'
                }`}
                data-testid={`nav-link-${id}`}
              >
                {link.label}
                {isActive && (
                  <span className="block h-0.5 bg-[#FF0000] mt-1 transition-all" />
                )}
              </a>
            );
          })}
        </nav>

        {/* Right side: phone + CTA */}
        <div className="hidden lg:flex items-center gap-5 flex-shrink-0">
          <a
            href="tel:+41788313353"
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
            data-testid="nav-phone"
          >
            <Phone className="w-4 h-4 text-[#FF0000]" />
            078 831 33 53
          </a>
          <a
            href="#devis"
            onClick={(e) => scrollTo(e, '#devis')}
            className="bg-[#FF0000] hover:bg-[#D90000] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all hover:scale-105"
            data-testid="nav-cta-devis"
          >
            Demander un devis
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu lg:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/5" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-4">
            {navLinks.map((link) => {
              const id = link.href.replace('#', '');
              const isActive = activeSection === id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollTo(e, link.href)}
                  className={`text-base font-medium uppercase tracking-widest py-2 transition-colors ${
                    isActive ? 'text-white' : 'text-white/50 hover:text-white'
                  }`}
                  data-testid={`mobile-nav-${id}`}
                >
                  {link.label}
                </a>
              );
            })}
            <a
              href="tel:+41788313353"
              className="flex items-center gap-2 text-white/70 hover:text-white text-base font-medium py-2 transition-colors"
              data-testid="mobile-phone"
            >
              <Phone className="w-4 h-4 text-[#FF0000]" />
              078 831 33 53
            </a>
            <a
              href="#devis"
              onClick={(e) => scrollTo(e, '#devis')}
              className="bg-[#FF0000] hover:bg-[#D90000] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest text-center mt-2 transition-all"
              data-testid="mobile-cta-devis"
            >
              Demander un devis
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
